import React from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Seal, EmptyState } from "../components/ui.jsx";
import { primaryBtnStyle, ghostBtnStyle } from "../components/styles.js";
import { fmtMoney, fmtDateShort, daysBetween, toISODate, today } from "../domain/format.js";
import { maintenanceStatus } from "../domain/preservation.js";
import { wearsThisMonth } from "../domain/analytics.js";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning,";
  if (h < 18) return "Good Afternoon,";
  return "Good Evening,";
}

export default function HomeDashboard({ items, displayName, onOpenAnalytics, onOpenItem, onGoTab, onOpenTimeline }) {
  const totalValue = items.reduce((s, i) => s + i.value, 0);
  const avgCondition = items.length ? Math.round(items.reduce((s, i) => s + i.condition, 0) / items.length) : 0;
  const flightsThisMonth = wearsThisMonth(items);
  const needCleaning = items.filter((i) => maintenanceStatus(i).cleanDue).length;

  const upcoming = items
    .map((i) => ({ item: i, ms: maintenanceStatus(i) }))
    .filter((x) => x.ms.nextCleanDate)
    .sort((a, b) => a.ms.nextCleanDate - b.ms.nextCleanDate)[0];

  const recentActivity = items
    .flatMap((i) => i.history.map((h) => ({ ...h, itemName: `${i.brand} ${i.model}` })))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  const cardStyle = { background: COLORS.blackCard, border: `1px solid ${COLORS.line}`, borderRadius: 16, padding: 18 };
  const labelStyle = { fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: 0.8, color: COLORS.creamFaint, textTransform: "uppercase" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>{greeting()}</div>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, color: COLORS.cream }}>{displayName}</div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="Your Passport is empty"
          body="Add your first item and SENOGARA starts tracking its condition, wear and preservation schedule."
          action={
            <button onClick={() => onGoTab("passport")} style={{ ...primaryBtnStyle, flex: "none", marginTop: 8, padding: "12px 22px" }}>
              + Add your first item
            </button>
          }
        />
      ) : (
        <>
          <div style={cardStyle}>
            <div style={labelStyle}>Collection Overview</div>
            <div style={{ display: "flex", gap: 26, marginTop: 10 }}>
              <div onClick={() => onGoTab("passport")} style={{ cursor: "pointer" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, color: COLORS.cream }}>{items.length}</div>
                <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>Items</div>
              </div>
              <div onClick={onOpenAnalytics} style={{ cursor: "pointer" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, color: COLORS.goldBright }}>{fmtMoney(totalValue)}</div>
                <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>Est. Value</div>
              </div>
            </div>
          </div>

          <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 16 }}>
            <Seal value={avgCondition} size={64} />
            <div>
              <div style={labelStyle}>Preservation Score</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, color: COLORS.cream, marginTop: 4 }}>
                {avgCondition >= 90 ? "Excellent" : avgCondition >= 70 ? "Good" : "Needs Attention"}
              </div>
            </div>
          </div>

          {/* The two stats go to different places, so the card itself is not
              clickable — a tap has to land on one of them to be unambiguous. */}
          <div style={cardStyle}>
            <div style={labelStyle}>Flight Tracker</div>
            <div style={{ display: "flex", gap: 26, marginTop: 10 }}>
              <div onClick={() => onGoTab("flight")} style={{ cursor: "pointer" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, color: COLORS.cream }}>{flightsThisMonth}</div>
                <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>Flights This Month</div>
              </div>
              <div onClick={() => onGoTab("passport")} style={{ cursor: "pointer" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, color: needCleaning ? COLORS.red : COLORS.cream }}>{needCleaning}</div>
                <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>Items Need Cleaning</div>
              </div>
            </div>
          </div>

          {upcoming && (
            <div onClick={() => onOpenItem(upcoming.item.id)} style={{ ...cardStyle, cursor: "pointer" }}>
              <div style={labelStyle}>Upcoming Reminder</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, color: COLORS.cream, marginTop: 6 }}>
                {upcoming.item.brand} {upcoming.item.model}
              </div>
              <div style={{ fontSize: 12, color: COLORS.creamDim, fontFamily: FONT_BODY, marginTop: 2 }}>
                Clean {upcoming.ms.cleanDue ? "now" : `in ${daysBetween(today(), toISODate(upcoming.ms.nextCleanDate))} days`}
              </div>
            </div>
          )}

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={labelStyle}>Recent Activity</span>
              <button onClick={onOpenTimeline} style={{ ...ghostBtnStyle, fontSize: 11.5, color: COLORS.goldBright }}>
                Legacy Timeline →
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recentActivity.length === 0 && <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>No activity yet.</div>}
              {recentActivity.map((a, i) => (
                <div key={a.id || i} style={{ fontSize: 12.5, fontFamily: FONT_BODY, color: COLORS.creamDim, display: "flex", justifyContent: "space-between" }}>
                  <span>
                    {a.action} — {a.itemName}
                  </span>
                  <span style={{ color: COLORS.creamFaint, fontFamily: FONT_MONO, fontSize: 11 }}>{fmtDateShort(a.date)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
